// Global Instructions Rule Applied!
//
// Deal form — Documents tab (signed-off Mockup 7, Phase 2 scope; upload folded
// in 2026-09-13): the cfg-driven required-documents tracker (generic
// requirements resolved by any of their satisfying document types, scoped by
// execution pipeline / deal type / security type / fund) plus the deal's files
// with category / type / lifecycle / version classification.
//
// This tab now OWNS deal files end to end — upload, open, classify, delete.
// The Dashboard Files card was removed in the same change, so there is exactly
// one place deal documents live and one list to keep fresh.
//
// Staleness: the app shell never unmounts tabs (TabContentWrapper hides them
// with display:none), so a mount-time-only fetch would go stale the moment a
// file changed. Three things prevent that here — every mutation on this tab
// refetches, `onRefreshReady` hands `refetch` up to the deal form so its
// Refresh button drives this tab too, and `useRefreshOnActivate` revalidates
// when the tab comes back into view (a requirement or type switched in
// Configuration shows up without a manual refresh).
//
// Requirement rows: the Document column opens the satisfying (or best partial)
// file through the same handleOpen the Files table uses. A document TYPE
// switched inactive in Configuration drops out of "Satisfied by any of" and
// the classify pick list, but a file already classified with it still
// satisfies and still shows its label (owner decision 2026-09-02).
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, InputNumber, Popconfirm, Progress, Select, Spin, Table, Tag } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import UploadModal from '../../../core/components/FileUploadManager/parts/UploadModal.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { addTab } from '../../../store/slices/tabSlice'
import { deleteFile, getSignedUrl, uploadFile } from '../../../core/services/filesService.ts'
import {
  FILE_TYPE,
  OFFICE_EXTENSIONS,
  OFFICE_VIEWER_URL,
  URL_TYPE
} from '../../../state_manager/constants/files.ts'
import { FIELD_DEAL_ID, TABLE_FILE_URLS_DEALS } from '../../../state_manager/globals.ts'
import {
  classifyDealFile,
  DealDocumentsData,
  fetchDealDocuments
} from '../services/dealExecutionService.ts'

interface DocumentsTabProps {
  dealId: string | null
  /** Hands `refetch` to the deal form so its Refresh button reloads this tab.
   *  Same contract FileUploadManager exposes via onRefreshRequest. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const LIFECYCLE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'execution', label: 'Execution' },
  { value: 'executed', label: 'Executed' }
]

const LIFECYCLE_COLORS: Record<string, string> = {
  draft: 'blue',
  execution: 'gold',
  executed: 'green'
}

function isMicrosoftOfficeFile(fileName: string): boolean {
  if (!fileName || typeof fileName !== 'string') return false
  const extension = fileName.toLowerCase().split('.').pop()
  return extension ? OFFICE_EXTENSIONS.includes(extension) : false
}

export default function DocumentsTab({
  dealId,
  onRefreshReady = null
}: DocumentsTabProps): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>()
  const token = () => getAccessToken(store.getState().auth)
  const [data, setData] = useState<DealDocumentsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // Category is a FILTER over the type list, not a stored field: a file's real
  // category is whatever its chosen type belongs to. Held per file id so
  // narrowing one row doesn't narrow the others.
  const [categoryFilter, setCategoryFilter] = useState<Record<string, string | null>>({})

  const refetch = useCallback(async () => {
    if (!dealId) return
    const result = await fetchDealDocuments(token(), dealId)
    if (result.error) showErrorToast(result.error)
    setData(result.data)
    setLoading(false)
  }, [dealId])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  const activateRef = useRefreshOnActivate(refetch)

  const satisfiedCount = useMemo(
    () => (data?.requirements || []).filter((r) => r.satisfied === true).length,
    [data]
  )

  const handleClassify = async (
    fileId: string,
    fields: { document_type_id?: string | null; lifecycle?: string | null; version?: number | null }
  ): Promise<void> => {
    const file = (data?.files || []).find((f) => f.id === fileId)
    const result = await classifyDealFile(token(), fileId, {
      document_type_id: (fields.document_type_id !== undefined
        ? fields.document_type_id
        : (file?.document_type_id as string | null)) ?? null,
      lifecycle: (fields.lifecycle !== undefined
        ? fields.lifecycle
        : (file?.lifecycle as string | null)) ?? null,
      version: fields.version !== undefined ? fields.version : ((file?.version as number | null) ?? null)
    })
    if (!result.success) showErrorToast(result.error || 'Failed to update classification')
    await refetch()
  }

  const handleUpload = async (uploadData: {
    type: typeof FILE_TYPE | typeof URL_TYPE
    files?: File[]
    url?: string
    fileName?: string
  }): Promise<void> => {
    if (!dealId) return
    setUploading(true)
    try {
      if (uploadData.type === FILE_TYPE && uploadData.files?.length) {
        // Sequential, so one bad file reports on its own rather than
        // collapsing the whole batch into a single failure.
        let failures = 0
        for (const file of uploadData.files) {
          try {
            await uploadFile(
              file, token(), dealId, null, FILE_TYPE, undefined, undefined,
              TABLE_FILE_URLS_DEALS, FIELD_DEAL_ID
            )
          } catch (e) {
            failures += 1
            showErrorToast(
              `${file.name}: ${e instanceof Error ? e.message : 'upload failed'}`
            )
          }
        }
        if (failures < uploadData.files.length) toast.success('File(s) uploaded')
      } else if (uploadData.type === URL_TYPE && uploadData.url && uploadData.fileName) {
        await uploadFile(
          null, token(), dealId, null, URL_TYPE, uploadData.url, uploadData.fileName,
          TABLE_FILE_URLS_DEALS, FIELD_DEAL_ID
        )
        toast.success('URL added')
      }
      setUploadOpen(false)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (fileId: string): Promise<void> => {
    setDeletingId(fileId)
    try {
      // Row-backed delete: the backend moves a storage object to
      // _trash/{date}/{row_id}/{filename} and trashes the row; a 'url' row is
      // just trashed. Both are recoverable by an operator.
      await deleteFile(null, token(), fileId, TABLE_FILE_URLS_DEALS)
      toast.success('File deleted')
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to delete file')
    } finally {
      setDeletingId(null)
    }
  }

  const handleOpen = async (file: Record<string, unknown>): Promise<void> => {
    const fileName = (file.file_name as string) || 'File'
    const filePath = (file.file_url as string) || ''
    if (!filePath) return
    try {
      // 'url' rows already hold a public link; storage rows need a short-lived
      // signed URL. Office formats route through the web viewer.
      let urlToOpen =
        file.type === URL_TYPE ? filePath : (await getSignedUrl(filePath, token())).signed_url
      if (isMicrosoftOfficeFile(fileName)) {
        urlToOpen = `${OFFICE_VIEWER_URL}${encodeURIComponent(urlToOpen)}`
      }
      const tabKey = `file-viewer-${filePath.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
      dispatch(
        addTab({
          id: tabKey,
          key: tabKey,
          title: fileName,
          path: `/file-viewer/${tabKey}`,
          type: 'file-viewer',
          module: 'file-viewer',
          closeable: true,
          fileUrl: urlToOpen,
          fileName
        })
      )
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to open file')
    }
  }

  if (loading || !data) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  const requirements = data.requirements || []
  const files = data.files || []
  const documentTypes = data.document_types || []
  const categoryOptions = (data.document_categories || []).map((c) => ({
    value: c.id,
    label: c.label
  }))

  // Types offered for a row: every ACTIVE type, unless a category is selected
  // for that row (explicitly, or implicitly by the type already on the file).
  // An inactive type already on the file stays in the list, marked, so the
  // cell shows its label rather than a bare id.
  const typeOptionsFor = (fileId: string, currentTypeId: string | null): { value: string; label: string }[] => {
    const explicit = categoryFilter[fileId]
    const implied =
      explicit === undefined
        ? (documentTypes.find((t) => t.id === currentTypeId)?.category_id as string | null) ?? null
        : explicit
    return documentTypes
      .filter((t) => !implied || t.category_id === implied)
      .filter((t) => t.active !== false || t.id === currentTypeId)
      .map((t) => ({ value: t.id, label: t.active === false ? `${t.label} (inactive)` : t.label }))
  }

  const linkedFileFor = (row: Record<string, unknown>): Record<string, unknown> | null =>
    (row.satisfied_by_file as Record<string, unknown> | null) ||
    (row.best_file as Record<string, unknown> | null) ||
    null

  const categoryValueFor = (fileId: string, currentTypeId: string | null): string | undefined => {
    const explicit = categoryFilter[fileId]
    if (explicit !== undefined) return explicit ?? undefined
    return (documentTypes.find((t) => t.id === currentTypeId)?.category_id as string | null) ?? undefined
  }

  return (
    <div ref={activateRef} className='px-2 py-4 space-y-4'>
      <FormSection
        title='Required Documents'
        titleColor='blue'
        scrollable={false}
        headerActions={
          <span className='flex items-center gap-3 text-xs text-gray-400'>
            <Progress
              percent={requirements.length ? Math.round((satisfiedCount / requirements.length) * 100) : 0}
              size='small'
              className='w-40 m-0'
              showInfo={false}
            />
            {satisfiedCount} of {requirements.length} satisfied
          </span>
        }
      >
        <Table
          size='small'
          rowKey='id'
          pagination={false}
          dataSource={requirements}
          columns={[
            {
              title: 'Requirement',
              dataIndex: 'name',
              render: (v, row) => (
                <span>
                  {v}
                  {((row.fund_names as string[]) || []).map((fundName) => (
                    <Tag key={fundName} className='ml-2' color='purple'>
                      {fundName}
                    </Tag>
                  ))}
                </span>
              )
            },
            {
              title: 'Satisfied by any of',
              dataIndex: 'satisfied_by_labels',
              render: (v) => {
                const labels = (v as string[]) || []
                return (
                  <span className='text-xs text-gray-400'>
                    {labels.length ? labels.join(' · ') : 'No active document types'}
                  </span>
                )
              }
            },
            {
              title: 'Needs',
              dataIndex: 'required_lifecycle',
              width: 110,
              render: (v) => <Tag color={LIFECYCLE_COLORS[String(v)] || 'default'}>{String(v)}</Tag>
            },
            {
              title: 'Document',
              key: 'document',
              render: (_, row) => {
                const file = linkedFileFor(row as Record<string, unknown>)
                if (!file) return <span className='text-gray-400'>—</span>
                return (
                  <button
                    type='button'
                    className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                    onClick={() => handleOpen(file)}
                  >
                    {(file.file_name as string) || 'File'}
                  </button>
                )
              }
            },
            {
              title: 'Status',
              key: 'status',
              width: 220,
              render: (_, row) => {
                if (row.satisfied) return <Tag color='green'>Satisfied ✓</Tag>
                const best = row.best_file as { lifecycle?: string } | null
                if (best) {
                  return (
                    <Tag color='gold'>
                      {best.lifecycle || 'draft'} on file · needs {String(row.required_lifecycle)}
                    </Tag>
                  )
                }
                return <Tag color='red'>Missing</Tag>
              }
            }
          ]}
        />
      </FormSection>

      <FormSection
        title='Files'
        titleColor='green'
        scrollable={false}
        headerActions={
          <span className='flex items-center gap-2'>
            <Button size='small' icon={<ReloadOutlined />} onClick={refetch}>
              Refresh
            </Button>
            <Button
              size='small'
              type='primary'
              icon={<UploadOutlined />}
              onClick={() => setUploadOpen(true)}
              disabled={!dealId}
            >
              Upload
            </Button>
          </span>
        }
      >
        <div className='text-xs text-gray-500 mb-2'>
          Upload files or links here, then classify each one (category · type · lifecycle · version)
        </div>
        {files.length === 0 ? (
          <div className='text-sm text-gray-400 py-3'>No files on this deal yet.</div>
        ) : (
          <Table
            size='small'
            rowKey='id'
            pagination={false}
            dataSource={files}
            columns={[
              {
                title: 'Name',
                dataIndex: 'file_name',
                render: (v, row) => (
                  <button
                    type='button'
                    className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                    onClick={() => handleOpen(row as Record<string, unknown>)}
                  >
                    {(v as string) || '—'}
                  </button>
                )
              },
              {
                title: 'Category',
                dataIndex: 'document_category_id',
                width: 200,
                render: (_, row) => (
                  <Select
                    size='small'
                    allowClear
                    className='w-full'
                    placeholder='All categories'
                    value={categoryValueFor(row.id as string, (row.document_type_id as string) ?? null)}
                    onChange={(value) => {
                      setCategoryFilter((prev) => ({ ...prev, [row.id as string]: value ?? null }))
                      // Changing category invalidates a type from another one.
                      const current = documentTypes.find((t) => t.id === row.document_type_id)
                      if (value && current && current.category_id !== value) {
                        handleClassify(row.id as string, { document_type_id: null })
                      }
                    }}
                    options={categoryOptions}
                  />
                )
              },
              {
                title: 'Type',
                dataIndex: 'document_type_id',
                width: 230,
                render: (v, row) => (
                  <Select
                    size='small'
                    allowClear
                    className='w-full'
                    placeholder='Classify…'
                    value={(v as string) ?? undefined}
                    onChange={(value) =>
                      handleClassify(row.id as string, { document_type_id: value ?? null })
                    }
                    options={typeOptionsFor(row.id as string, (v as string) ?? null)}
                  />
                )
              },
              {
                title: 'Lifecycle',
                dataIndex: 'lifecycle',
                width: 140,
                render: (v, row) => (
                  <Select
                    size='small'
                    allowClear
                    className='w-full'
                    placeholder='—'
                    value={(v as string) ?? undefined}
                    onChange={(value) => handleClassify(row.id as string, { lifecycle: value ?? null })}
                    options={LIFECYCLE_OPTIONS}
                  />
                )
              },
              {
                title: 'Ver.',
                dataIndex: 'version',
                width: 80,
                render: (v, row) => (
                  <InputNumber
                    size='small'
                    min={1}
                    value={(v as number) ?? undefined}
                    onBlur={(e) => {
                      const raw = (e.target as HTMLInputElement).value
                      const next = raw === '' ? null : Number(raw)
                      if (next !== ((v as number) ?? null)) {
                        handleClassify(row.id as string, { version: next })
                      }
                    }}
                  />
                )
              },
              {
                title: 'Uploaded',
                dataIndex: 'date_created',
                width: 110,
                render: (v) => (v ? dayjs(String(v)).format('MM/DD/YYYY') : '—')
              },
              {
                title: '',
                key: 'actions',
                width: 50,
                render: (_, row) => (
                  <Popconfirm
                    title='Delete this file?'
                    description='The file moves to the trash namespace and drops off this deal.'
                    okText='Delete'
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(row.id as string)}
                  >
                    <Button
                      size='small'
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      loading={deletingId === row.id}
                    />
                  </Popconfirm>
                )
              }
            ]}
          />
        )}
      </FormSection>

      <UploadModal
        visible={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onUpload={handleUpload}
        loading={uploading}
      />
    </div>
  )
}
